import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faBookmark,
  faCheck,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { useI18n, useT } from "../../i18n/I18nContext";
import { relativeTime } from "../../util/relativeTime";

// Reading-shelf card: cover, title, last-read relative time,
// parts-remaining progress. Shown in the "Your stories" / "Vos
// histoires" grid on /browse.
//
// Three reading states drive the chrome:
//   * not started (safeRead === 0) — "New" pill, total parts caption
//   * in progress (safeLeft > 0)   — "Reading" pill, parts-left caption,
//                                    progress bar with % label
//   * finished (safeLeft === 0 with chapters) — "Finished" pill,
//                                    full progress bar, no caption
//
// Visual language matches RecommendBookCard (the poster sitting next
// to this on /browse): aspect-[2/3] cover fills the card, dark
// gradient at the bottom keeps text readable, status pill rides the
// top-right. The reading-specific extras (last-read time, parts
// caption, progress bar) layer over the cover so the card reads as
// a sibling of the recommendation poster rather than a different
// component family.
const BookCard = ({
  img,
  title,
  lastReadIso,
  partsRead,
  partLeft,
  totalParts,
  projectId,
}) => {
  const t = useT();
  const { locale } = useI18n();

  const safeTotal = Number(totalParts) || 0;
  const safeRead = Math.max(0, Math.min(safeTotal, Number(partsRead) || 0));
  const safeLeft = Math.max(0, Math.min(safeTotal, Number(partLeft) || 0));
  const progressPct = safeTotal > 0 ? (safeRead / safeTotal) * 100 : 0;

  // Reading-state classification — drives the pill + caption.
  let state;
  if (safeTotal === 0) state = "empty";
  else if (safeRead === 0) state = "new";
  else if (safeLeft === 0) state = "finished";
  else state = "reading";

  const pillFor = (s) => {
    if (s === "finished") {
      return {
        icon: faCheck,
        label: t("home.book.state.finished"),
        // Pill chrome matches RecommendBookCard's top-right meta:
        // blurred dark background so it reads as an overlay, with
        // a coloured icon for the state cue.
        iconClass: "text-success",
      };
    }
    if (s === "reading") {
      return {
        icon: faBookmark,
        label: t("home.book.state.reading"),
        iconClass: "text-primary",
      };
    }
    if (s === "new") {
      return {
        icon: faClock,
        label: t("home.book.state.new"),
        iconClass: "text-accent",
      };
    }
    return null;
  };
  const pill = pillFor(state);

  // Parts caption — kept only for the unambiguous states. The
  // active-reading state used to show "Chapter X of Y", but X was
  // derived from progress_percent × published_chapter_count, which
  // didn't match what the writer thought of as their chapter count
  // (drafts included). The progress bar + percentage below already
  // conveys "how far in", so the redundant X-of-Y caption now drops
  // out for the reading state — fewer ways to be wrong.
  let partsCaption;
  if (state === "empty") {
    partsCaption = t("home.book.no_chapters");
  } else if (state === "new") {
    partsCaption = t("home.book.parts_total", { count: safeTotal });
  } else if (state === "finished") {
    partsCaption = t("home.book.finished");
  } else {
    partsCaption = null;
  }

  return (
    <Link
      to={`/read/${projectId}`}
      aria-label={title}
      className="group relative block w-full aspect-[2/3] rounded-2xl overflow-hidden border border-border bg-surfaceAlt shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
    >
      {/* Cover image — fills the card. Falls back to the book icon
          on missing src; matches RecommendBookCard's placeholder. */}
      {img ? (
        <img
          src={img}
          alt=""
          aria-hidden="true"
          loading="lazy"
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-secondText">
          <FontAwesomeIcon icon={faBookOpen} className="text-4xl" aria-hidden="true" />
        </div>
      )}

      {/* Gradient overlay — bottom half darkens for text legibility,
          top stays transparent so the cover art reads first. */}
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none"
        aria-hidden="true"
      />

      {/* Top-right state pill — same blurred-dark overlay chrome as
          RecommendBookCard's rating/views chips so the two cards
          share a meta-row language. */}
      {pill ? (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 text-[11px] font-semibold">
          <FontAwesomeIcon icon={pill.icon} className={`text-[10px] ${pill.iconClass}`} />
          {pill.label}
        </span>
      ) : null}

      {/* Bottom overlay — title + last-read + parts caption + (if
          actively reading) a thin progress bar. ``line-clamp-2`` on
          the title so a long one doesn't blow the bottom layout. */}
      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <h3
          className="text-sm font-semibold leading-tight line-clamp-2 mb-1 group-hover:text-primary transition"
          title={title}
        >
          {title}
        </h3>
        <p className="text-[11px] text-white/80 truncate">
          {lastReadIso
            ? t("home.book.last_read", { date: relativeTime(lastReadIso, locale) })
            : t("home.book.not_started")}
        </p>
        {partsCaption ? (
          <p className="text-[11px] text-white/80 tabular-nums truncate">
            {partsCaption}
          </p>
        ) : null}

        {/* Progress bar — only meaningful for projects with chapters
            in an actively-reading or finished state. The bar tucks
            below the caption row so the text hierarchy stays
            title → meta → progress. */}
        {safeTotal > 0 && state !== "new" ? (
          <div className="mt-2 flex items-center gap-2">
            <div className="relative overflow-hidden h-1 bg-white/20 rounded-full flex-1">
              <div
                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${
                  state === "finished" ? "bg-success" : "bg-primary"
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-[10px] text-white/80 tabular-nums shrink-0">
              {Math.round(progressPct)}%
            </span>
          </div>
        ) : null}
      </div>
    </Link>
  );
};

export default BookCard;
